import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Plus, Search, Wrench } from "lucide-react";
import { useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { getSession } from "@/lib/session";
import {
  createOrder,
  listOrders,
  STATUS_LABEL,
  STATUS_ORDER,
  STATUS_TONE,
  type OrderCreate,
  type OrderDetailPublic,
} from "@/lib/workshop";

/**
 * Tablero del taller.
 *
 * Las columnas son los estados por los que pasa una reparación, en el orden en
 * que avanza. No es un adorno: en un taller la pregunta de las nueve de la
 * mañana es «¿qué hay pendiente?», y la respuesta tiene que verse de un golpe.
 */
export function WorkshopScreen({ onOpenOrder }: { onOpenOrder: (orderId: string) => void }) {
  const [term, setTerm] = useState("");
  const [creando, setCreando] = useState(false);
  const queryClient = useQueryClient();

  const ordenes = useQuery({
    queryKey: ["workshop", "orders", term],
    queryFn: () => listOrders({ q: term || undefined, limit: 100 }),
  });

  const porEstado = new Map<string, OrderDetailPublic[]>();
  for (const detalle of ordenes.data?.items ?? []) {
    const lista = porEstado.get(detalle.order.status) ?? [];
    lista.push(detalle);
    porEstado.set(detalle.order.status, lista);
  }

  const columnas = STATUS_ORDER.filter(
    // Las columnas vacías de los estados finales se ocultan: si no, el tablero
    // se llena de huecos y deja de leerse de un golpe.
    (status) =>
      (porEstado.get(status)?.length ?? 0) > 0 ||
      !["entregado", "cancelado", "rechazado"].includes(status),
  );

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-64 flex-1">
          <span className="sr-only">Buscar por folio, serie o cliente</span>
          <Search aria-hidden className="absolute top-3 left-3 size-4 text-muted" />
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Buscar por folio, número de serie o cliente…"
            className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow pr-4 pl-9 text-carbon placeholder:text-muted focus:outline-none"
          />
        </label>
        <Button size="lg" onClick={() => setCreando(true)} icon={<Plus aria-hidden className="size-4" />}>
          Recibir equipo
        </Button>
      </header>

      {ordenes.isPending ? (
        <p className="text-muted">Cargando órdenes…</p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columnas.map((status) => {
            const lista = porEstado.get(status) ?? [];
            return (
              <section key={status} className="min-w-64 flex-1 space-y-2">
                <h2 className="flex items-center justify-between gap-2 text-sm font-semibold text-carbon">
                  <StatusPill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</StatusPill>
                  <span className="tabular text-muted">{lista.length}</span>
                </h2>
                {lista.length === 0 ? (
                  <p className="rounded-msr border border-dashed border-line px-3 py-6 text-center text-xs text-muted">
                    Sin órdenes
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {lista.map((detalle) => (
                      <li key={detalle.order.id}>
                        <button
                          type="button"
                          onClick={() => onOpenOrder(detalle.order.id)}
                          className="w-full rounded-msr border border-line bg-snow p-3 text-left transition-colors hover:border-electric/60 hover:bg-surface"
                        >
                          <p className="flex items-center justify-between font-mono text-xs text-muted">
                            <span>
                              {detalle.order.series}-{detalle.order.folio}
                            </span>
                            {detalle.order.priority !== "normal" ? (
                              <StatusPill
                                tone={detalle.order.priority === "urgente" ? "danger" : "warning"}
                              >
                                {detalle.order.priority}
                              </StatusPill>
                            ) : null}
                          </p>
                          <p className="mt-1 truncate font-medium text-carbon">
                            {detalle.order.brand} {detalle.order.model}
                          </p>
                          <p className="truncate text-xs text-muted">
                            {detalle.customer.name}
                          </p>
                          <p className="mt-1 line-clamp-2 text-xs text-muted">
                            {detalle.order.reported_issue}
                          </p>
                          {detalle.estimate ? (
                            <p className="mt-1 text-sm font-semibold text-carbon">
                              <Money cents={detalle.estimate.total_cents} size="sm" />
                            </p>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {ordenes.data?.items.length === 0 && !term ? (
        <Card>
          <CardHeader
            title="Todavía no hay órdenes"
            description="Recibe un equipo para abrir la primera. El folio se asigna solo."
            actions={<ClipboardList aria-hidden className="size-5 text-muted" />}
          />
        </Card>
      ) : null}

      {creando ? (
        <NewOrderDialog
          onClose={() => setCreando(false)}
          onCreated={(detalle) => {
            setCreando(false);
            void queryClient.invalidateQueries({ queryKey: ["workshop"] });
            onOpenOrder(detalle.order.id);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Recepción de un equipo.
 *
 * El campo importante es el de la avería **según el cliente**: es la única
 * prueba de cómo llegó el equipo y el servidor no deja reescribirlo después.
 */
function NewOrderDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (detalle: OrderDetailPublic) => void;
}) {
  const session = getSession();
  const branch = session?.branches[0] ?? null;
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    device_type: "celular",
    brand: "",
    model: "",
    serial_number: "",
    reported_issue: "",
    accessories: "",
    physical_condition: "",
    unlock_code: "",
    priority: "normal" as OrderCreate["priority"],
    warranty_days: 90,
  });

  const crear = useMutation({
    mutationFn: () =>
      createOrder({
        branch_id: branch?.id ?? "",
        customer_name: form.customer_name,
        customer_phone: form.customer_phone || null,
        device_type: form.device_type,
        brand: form.brand || null,
        model: form.model || null,
        serial_number: form.serial_number || null,
        reported_issue: form.reported_issue,
        accessories: form.accessories || null,
        physical_condition: form.physical_condition || null,
        unlock_code: form.unlock_code || null,
        priority: form.priority,
        warranty_days: form.warranty_days,
      }),
    onSuccess: onCreated,
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "No se pudo abrir la orden"),
  });

  function campo(key: keyof typeof form, value: string | number) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center overflow-auto bg-black/60 p-4">
      <form
        className="w-full max-w-3xl space-y-5 rounded-msr-lg border border-line bg-snow p-6 text-carbon shadow-2xl"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          crear.mutate();
        }}
      >
        <header className="flex items-center gap-3">
          <Wrench aria-hidden className="size-5 text-electric" />
          <div>
            <h2 className="font-display text-xl font-bold text-carbon">Recibir equipo</h2>
            <p className="text-xs text-muted">
              Se asigna folio y se copia el checklist del tipo de equipo.
            </p>
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Cliente" value={form.customer_name} onChange={(v) => campo("customer_name", v)} required />
          <Field
            label="Teléfono"
            value={form.customer_phone}
            onChange={(v) => campo("customer_phone", v)}
            placeholder="5512345678"
          />
          <Field label="Tipo de equipo" value={form.device_type} onChange={(v) => campo("device_type", v)} required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Marca" value={form.brand} onChange={(v) => campo("brand", v)} />
            <Field label="Modelo" value={form.model} onChange={(v) => campo("model", v)} />
          </div>
          <Field
            label="Número de serie o IMEI"
            value={form.serial_number}
            onChange={(v) => campo("serial_number", v)}
          />
          <Field
            label="Clave de desbloqueo"
            value={form.unlock_code}
            onChange={(v) => campo("unlock_code", v)}
            hint="Sólo la ven quienes reparan."
          />
        </div>

        <Field
          label="Avería según el cliente"
          value={form.reported_issue}
          onChange={(v) => campo("reported_issue", v)}
          required
          multiline
          hint="Es la versión del cliente y no se puede reescribir después. Es la única prueba de cómo llegó el equipo."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Accesorios que deja"
            value={form.accessories}
            onChange={(v) => campo("accessories", v)}
            placeholder="Funda, cargador…"
          />
          <Field
            label="Estado estético al recibir"
            value={form.physical_condition}
            onChange={(v) => campo("physical_condition", v)}
            placeholder="Rayones, golpes…"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs tracking-wide text-muted uppercase">
              Prioridad
            </span>
            <select
              value={form.priority}
              onChange={(event) => campo("priority", event.target.value)}
              className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow px-3 text-carbon focus:outline-none"
            >
              <option value="baja">Baja</option>
              <option value="normal">Normal</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </label>
          <Field
            label="Garantía (días)"
            value={String(form.warranty_days)}
            onChange={(v) => campo("warranty_days", Number.parseInt(v || "0", 10))}
            type="number"
          />
        </div>

        {error ? (
          <p role="alert" className="rounded-msr border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex gap-3">
          <Button variant="secondary" size="lg" fullWidth onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="lg" fullWidth loading={crear.isPending}>
            Recibir y abrir orden
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  multiline,
  hint,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  multiline?: boolean;
  hint?: string;
  placeholder?: string;
  type?: string;
}) {
  const clases =
    "focus:border-electric w-full rounded-msr border border-line bg-snow px-3 py-2 text-carbon placeholder:text-muted focus:outline-none";
  return (
    <label className="block">
      <span className="mb-1 block text-xs tracking-wide text-muted uppercase">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          rows={3}
          placeholder={placeholder}
          className={clases}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          placeholder={placeholder}
          className={`${clases} h-11`}
        />
      )}
      {hint ? <span className="mt-1 block text-[11px] text-muted">{hint}</span> : null}
    </label>
  );
}
